import {useEffect} from 'react'
import {useParams} from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import Details from '../../shared/Details'

import {connect} from '../../../state/hooks/tools'
import {findRecordByAttribute} from '../../../lib/data'
import {fullName, isEmpty} from '../../../lib/tools'
import {createResource, readEndpoint, updateResource} from '../../../lib/asyncMethods'
import {DISABLED, ENABLED, TFA_BYPASS, isTfaBypassed, tfaBypassFlag} from '../../../lib/featureFlags'
import {USERS, SETTINGS, TFA_CONFIGS} from '../../../state/actionTypes'

/**
 * Why the bypass can or cannot be granted, in the words an admin needs before clicking.
 *
 * The users listing is paginated, so the account already holding the flag is often not among the
 * records this page was handed — naming it is a courtesy, and its absence must not read as nobody.
 */
const bypassNotice = ({bypassed, holder, heldElsewhere}) => {
  if (heldElsewhere)
    return `Only one account can bypass 2FA, and ${isEmpty(holder) ? 'another account' : fullName(holder)} holds it`

  if (bypassed)
    return 'Ask this user for a second factor again'

  return 'Let this user sign in on the password alone, and skip 2FA setup'
}

/**
 * An account already carrying a second factor has nothing to be exempted from, so it is not offered
 * one. A live bypass stays revocable either way: the flag holds the only slot the system allows, and
 * hiding its off switch would strand it there turned on.
 */
const offersTfaBypass = ({tfa, bypassed}) => bypassed || tfa.status !== 'enabled'

const FooterActions = props => (
  <Button onClick={props.goBack} sx={{bgcolor: 'Background'}}>
    <ArrowBackIcon />
    <Typography variant='span'>Back</Typography>
  </Button>
)

const HeaderActions = props => (
  <Stack direction='row' sx={{height: '41px'}}>
    <Button disabled={props.loading} onClick={props.blockLogIn} sx={{bgcolor: 'Background'}}>
      <Typography variant='span'>{props.data?.blockLogin ? 'Enable' : 'Disable'} LogIn</Typography>
    </Button>
    {
      props.tfa.status === 'enabled' && (
        <Button disabled={props.loading} sx={{bgcolor: 'Background', marginLeft: '8px'}}>
          <Typography variant='span'>Disable 2FA</Typography>
        </Button>
      )
    }
    {
      offersTfaBypass(props) && (
        <Tooltip title={props.bypassNotice}>
          <span>
            <Button
              disabled={props.loading || props.heldElsewhere}
              onClick={props.toggleTfaBypass}
              sx={{bgcolor: 'Background', marginLeft: '8px'}}>
              <Typography variant='span'>{props.bypassed ? 'Require' : 'Bypass'} 2FA</Typography>
            </Button>
          </span>
        </Tooltip>
      )
    }
  </Stack>
)

const User = props => {
  const params = useParams()
  const user = findRecordByAttribute({records: props.users, attribute: 'id', id: params.id})
  const tfa = findRecordByAttribute({records: props.tfaConfigs, attribute: 'userId', id: params.id})
  const bypassFlag = tfaBypassFlag(props.settings)
  const bypassed = isTfaBypassed({settings: props.settings, userId: params.id})
  // a row turned off still occupies the one slot the database allows, so the value is no part of this
  const heldElsewhere = Boolean(bypassFlag.ownableId) && bypassFlag.ownableId !== params.id
  const holder = heldElsewhere ? findRecordByAttribute({records: props.users, attribute: 'id', id: bypassFlag.ownableId}) : {}

  useEffect(() => {
    if (isEmpty(props.settings))
      props.readSettings('settings')
  }, [])

  const blockLogIn = () => {
    props.updateResource({endpoint: `users/${user.id}/access`, attributes: {blockLogin: !user.blockLogin}})
  }

  const disableTfa = () => {
    props.updateTfa({endpoint: `tfa-configs/${tfa.id}/disable`})
  }

  const toggleTfaBypass = () => {
    if (isEmpty(bypassFlag))
      props.createSetting({endpoint: 'settings', attributes: {name: TFA_BYPASS, featureFlag: true, value: ENABLED, ownableId: user.id}})
    else
      props.updateSetting({endpoint: `settings/${bypassFlag.id}`, attributes: {value: bypassed ? DISABLED : ENABLED}})
  }

  return (
    <Details
      data={user}
      tfa={tfa}
      blockLogIn={blockLogIn}
      disableTfa={disableTfa}
      bypassed={bypassed}
      heldElsewhere={heldElsewhere}
      bypassNotice={bypassNotice({bypassed, holder, heldElsewhere})}
      toggleTfaBypass={toggleTfaBypass}
      loading={props.isLoading}
      headerActions={(user.id !== props.currentUser.id) && HeaderActions}
      editActions={FooterActions}
    />
  )
}

const mapStateToProps = ({users, tfa, metadata}) => ({
  ...tfa,
  ...users,
  settings: metadata.settings,
  isLoading: tfa.loading || users.loading || metadata.loading
})

const actionCreators = ({tfa, users, metadata}) => ({
  updateTfa: updateResource(tfa, TFA_CONFIGS),
  updateResource: updateResource(users, USERS),
  readSettings: readEndpoint(metadata, SETTINGS),
  createSetting: createResource(metadata, SETTINGS),
  updateSetting: updateResource(metadata, SETTINGS)
})

export default connect(mapStateToProps, actionCreators)(User)
